import { z } from 'zod';
export const RoleSchema = z.enum([
    'master_admin',
    'tenant_admin',
    'tenant_manager',
    'tenant_viewer',
    'resident',
]);
export const CapabilitySchema = z.enum([
    'WITH_MASTER_SCOPE',
    'TENANT_MANAGE_USERS',
    'TENANT_MANAGE_EMPLOYEES',
    'TENANT_MANAGE_SCHEDULES',
    'TENANT_MANAGE_GROUPS',
    'TENANT_READ_AUDIT',
    'TENANT_READ_EVENTS',
    'TENANT_UNLOCK_DOOR',
    'MASTER_TENANT_CRUD',
    'MASTER_HARDWARE_SCOPE',
    'MASTER_LOCKDOWN',
    'MASTER_READ_AUDIT_ALL',
    // Master-only: branding + terminology overrides (logo, brand color,
    // resident/employee/building labels). Read is public (drives sign-in
    // chrome before auth); write requires this capability.
    'MASTER_MANAGE_BRANDING',
    // Master-only: credential policy + super-admin controls for third-party
    // and touch credential enrollment (PRs #54/#55).
    'MASTER_CREDENTIAL_ADMIN',
    // Operator override on the durable retry queue — retry / delete jobs
    // can suppress controller reconciliation, so the surface gets its own
    // capability instead of riding on the broad MASTER_TENANT_CRUD.
    'MASTER_OUTBOX_ADMIN',
    // Multi-family residential surfaces. Property + amenity + callbox CRUD
    // is for tenant_admin (the property's management). Residents read +
    // edit their own profile + issue guest passes via TENANT_MANAGE_GUEST_PASS.
    'TENANT_MANAGE_PROPERTY',
    'TENANT_MANAGE_RESIDENTS',
    'TENANT_MANAGE_AMENITIES',
    'TENANT_MANAGE_CALLBOX',
    // Live intercom call lifecycle: queued -> answered -> ended (or missed).
    // Operators with this capability can claim queued calls and end active
    // calls. Tenant-scoped — answers are recorded against the tenant whose
    // callbox triggered the call.
    'TENANT_MANAGE_INTERCOM',
    // Time-bound vendor access lifecycle (grant / revoke). Restricted to
    // master + tenant_admin since vendor grants are sensitive (third-party
    // physical access to the property).
    'TENANT_MANAGE_VENDORS',
    'TENANT_MANAGE_GUEST_PASS',
    'RESIDENT_SELF_SERVICE',
    // Resident-scoped subset of guest-pass management — caller can only
    // sponsor/list/revoke passes whose sponsor_profile matches their own
    // ResidentProfile. Used on /me/guest-passes. Reviewer Tranche V #5.
    'RESIDENT_MANAGE_OWN_GUEST_PASS',
]);
export const ROLE_CAPABILITIES = {
    master_admin: [
        'WITH_MASTER_SCOPE',
        'MASTER_TENANT_CRUD',
        'MASTER_HARDWARE_SCOPE',
        'MASTER_LOCKDOWN',
        'MASTER_READ_AUDIT_ALL',
        'MASTER_CREDENTIAL_ADMIN',
        'MASTER_OUTBOX_ADMIN',
        'MASTER_MANAGE_BRANDING',
        'TENANT_MANAGE_USERS',
        'TENANT_MANAGE_EMPLOYEES',
        'TENANT_MANAGE_SCHEDULES',
        'TENANT_MANAGE_GROUPS',
        'TENANT_READ_AUDIT',
        'TENANT_READ_EVENTS',
        'TENANT_UNLOCK_DOOR',
        'TENANT_MANAGE_PROPERTY',
        'TENANT_MANAGE_RESIDENTS',
        'TENANT_MANAGE_AMENITIES',
        'TENANT_MANAGE_CALLBOX',
        'TENANT_MANAGE_INTERCOM',
        'TENANT_MANAGE_VENDORS',
        'TENANT_MANAGE_GUEST_PASS',
    ],
    tenant_admin: [
        'TENANT_MANAGE_USERS',
        'TENANT_MANAGE_EMPLOYEES',
        'TENANT_MANAGE_SCHEDULES',
        'TENANT_MANAGE_GROUPS',
        'TENANT_READ_AUDIT',
        'TENANT_READ_EVENTS',
        'TENANT_UNLOCK_DOOR',
        'TENANT_MANAGE_PROPERTY',
        'TENANT_MANAGE_RESIDENTS',
        'TENANT_MANAGE_AMENITIES',
        'TENANT_MANAGE_CALLBOX',
        'TENANT_MANAGE_INTERCOM',
        'TENANT_MANAGE_VENDORS',
        'TENANT_MANAGE_GUEST_PASS',
    ],
    tenant_manager: [
        'TENANT_MANAGE_EMPLOYEES',
        'TENANT_MANAGE_SCHEDULES',
        'TENANT_MANAGE_GROUPS',
        'TENANT_READ_EVENTS',
        'TENANT_UNLOCK_DOOR',
        'TENANT_MANAGE_RESIDENTS',
        'TENANT_MANAGE_CALLBOX',
        'TENANT_MANAGE_INTERCOM',
        'TENANT_MANAGE_GUEST_PASS',
    ],
    tenant_viewer: ['TENANT_READ_EVENTS', 'TENANT_READ_AUDIT'],
    // Residents see + manage their own profile and can sponsor guest passes
    // ONLY for their own household, via the /me/guest-passes self-service
    // surface. They do NOT get TENANT_MANAGE_GUEST_PASS (which would expose
    // every guest pass in the property — reviewer Tranche V #5).
    resident: ['RESIDENT_SELF_SERVICE', 'RESIDENT_MANAGE_OWN_GUEST_PASS'],
};
export function hasCapability(role, capability) {
    // role is a typed-union value, ROLE_CAPABILITIES has every Role as a key.
    // eslint-disable-next-line security/detect-object-injection
    return ROLE_CAPABILITIES[role].includes(capability);
}
//# sourceMappingURL=roles.js.map